import React, { useState, useEffect } from 'react';
import { Link2, Plus, Trash2, RefreshCw, CheckCircle, XCircle, Globe, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export const ExternalInstitutions = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [testingId, setTestingId] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({ name: '', base_url: '', email: '', password: '' });
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchSources(); }, []);

  const fetchSources = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/groups/my-group/external-sources`, { headers });
      setSources(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name || !form.base_url || !form.email || !form.password) {
      return toast.error('Please fill all fields');
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/groups/my-group/external-sources`, form, { headers });
      toast.success('External institution linked! Its financials will now merge into group reports.');
      setShowModal(false);
      setForm({ name: '', base_url: '', email: '', password: '' });
      fetchSources();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link institution');
    } finally { setSaving(false); }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const res = await axios.post(`${API_URL}/api/groups/my-group/external-sources/${id}/test`, {}, { headers });
      setTestResults(prev => ({ ...prev, [id]: res.data }));
      if (res.data.status === 'connected') toast.success('Connection OK!');
      else toast.error('Connection failed');
    } catch (e) { toast.error('Test failed'); }
    finally { setTestingId(null); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Unlink "${name}"? Its data will no longer appear in group reports.`)) return;
    try {
      await axios.delete(`${API_URL}/api/groups/my-group/external-sources/${id}`, { headers });
      toast.success('Institution unlinked');
      fetchSources();
    } catch (e) { toast.error('Failed to unlink'); }
  };

  return (
    <div className="bg-white rounded-xl shadow mt-8" data-testid="external-institutions-section">
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-purple-600" />
          <div>
            <h3 className="font-bold">External Institutions</h3>
            <p className="text-xs text-gray-500">Link other school/college software — their Income, P&amp;L and Balance Sheet will merge into group reports</p>
          </div>
        </div>
        <button data-testid="add-external-institution-btn" onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">
          <Plus className="h-4 w-4" /> Link Institution
        </button>
      </div>

      {loading ? <div className="p-6 text-center text-gray-400 text-sm">Loading...</div> : sources.length === 0 ? (
        <div className="p-6 text-center text-gray-400 text-sm">No external institutions linked yet</div>
      ) : (
        <div className="divide-y">
          {sources.map(src => {
            const test = testResults[src.id];
            return (
              <div key={src.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`external-source-${src.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-purple-500" />
                    <span className="font-semibold">{src.name}</span>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">External</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{src.base_url} · {src.email}</p>
                  {test && (
                    <p className={`text-xs mt-1 flex items-center gap-1 ${test.status === 'connected' ? 'text-green-600' : 'text-red-600'}`}>
                      {test.status === 'connected'
                        ? <><CheckCircle className="h-3 w-3" /> Connected — This month income ₹{(test.sample?.month_income || 0).toLocaleString('en-IN')}, Assets ₹{(test.sample?.total_assets || 0).toLocaleString('en-IN')}</>
                        : <><XCircle className="h-3 w-3" /> {test.error}</>}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button data-testid={`test-external-${src.id}`} onClick={() => handleTest(src.id)} disabled={testingId === src.id}
                    className="flex items-center gap-1 px-3 py-1.5 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 text-xs disabled:opacity-50">
                    <RefreshCw className={`h-3 w-3 ${testingId === src.id ? 'animate-spin' : ''}`} /> Test Connection
                  </button>
                  <button data-testid={`delete-external-${src.id}`} onClick={() => handleDelete(src.id, src.name)}
                    className="flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-xs">
                    <Trash2 className="h-3 w-3" /> Unlink
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" data-testid="external-institution-modal">
            <h3 className="font-bold text-lg mb-1">Link External Institution</h3>
            <p className="text-xs text-gray-500 mb-4">Enter the other software's URL and admin login. We'll verify the connection before saving.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Institution Name *</label>
                <input data-testid="external-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Morning Star School" className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Software URL *</label>
                <input data-testid="external-url-input" value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })}
                  placeholder="https://cloudcampus.co.in" className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Admin Login Email *</label>
                <input data-testid="external-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="admin@school.com" className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Password *</label>
                <div className="relative">
                  <input data-testid="external-password-input" type={showPwd ? 'text' : 'password'} value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm pr-9" />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-2 text-gray-400">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">Stored encrypted. Used only to fetch financial reports.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button data-testid="external-save-btn" onClick={handleSave} disabled={saving}
                className="flex-1 bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm">
                {saving ? 'Verifying & Saving...' : 'Verify & Link'}
              </button>
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalInstitutions;
